/**
 * orderAlerts.ts — Aviso global de pedido nuevo (beep + voz + toast + notificación).
 *
 * QUÉ HACE: Punto único para avisar que llegó un pedido nuevo (mesa, domicilio
 *   o tienda «local»). Aplica las preferencias del negocio
 *   (`lib/audio/posSoundPrefs`): beep (`playPosPendingBeep`), mensaje en la
 *   esquina con `react-hot-toast`, lectura del mensaje personalizado + detalle
 *   por voz (`speak`) y notificación del sistema (categoría «ventas», respeta
 *   `notificationPrefsStore`). El mensaje lo escribe el usuario en su idioma:
 *   no hay traducciones.
 * QUÉ EXPORTA: notifyOrderAlert.
 * QUÉ NO DEBE HACER: No persiste preferencias, no conoce React ni decide qué
 *   pedidos son nuevos (eso lo hace el watcher que lo invoca).
 */
import toast from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { isNotificationCategoryEnabled } from "@/store/notificationPrefsStore";
import { sendMobileNotification } from "@/lib/push/sendMobileNotification";
import { buildCategoryNotification } from "@/lib/push/mobileNotificationDesign";
import {
  loadPosSoundPrefs,
  type PosSoundPrefs,
} from "@/lib/audio/posSoundPrefs";
import { playPosPendingBeep, speak } from "@/lib/audio/appSounds";

type OrderAlertKind = "mesa" | "domicilio" | "local";

interface OrderAlertInfo {
  /** Texto extra para voz, toast y notificación (cliente, mesa, mesero…). */
  detalle?: string;
  /** ID del pedido para el deep-link de la notificación. */
  pedidoId?: string;
  /** Pantalla destino del deep-link (ej. "caja" o "tienda/pedidos"). */
  screen?: string;
}

/** Mensaje personalizado del negocio según el tipo de pedido. */
function orderAlertMessage(prefs: PosSoundPrefs, kind: OrderAlertKind): string {
  if (kind === "mesa") return prefs.mensajeMesa;
  if (kind === "domicilio") return prefs.mensajeDomicilio;
  return prefs.mensajeLocal;
}

/**
 * Dispara el aviso completo del pedido nuevo según las preferencias activas.
 * Best-effort: nunca lanza errores ni bloquea al llamador.
 */
export function notifyOrderAlert(
  kind: OrderAlertKind,
  info: OrderAlertInfo = {},
): void {
  try {
    const businessId = useAuthStore.getState().user?.businessId;
    const prefs = loadPosSoundPrefs(businessId);
    const mensaje = orderAlertMessage(prefs, kind);
    const detalle = info.detalle?.trim() ?? "";

    if (prefs.pending) {
      playPosPendingBeep(prefs.volume);
      // Mensaje en la esquina al instante (mismo instante que el beep).
      toast(detalle ? `${mensaje} · ${detalle}` : mensaje, {
        id: `order-alert-${info.pedidoId ?? kind}`,
        duration: 6000,
      });
    }

    if (prefs.voice) {
      speak(
        [mensaje, detalle].filter((p) => p.length > 0).join(". "),
        prefs.volume,
      );
    }

    if (prefs.notify && isNotificationCategoryEnabled("ventas")) {
      void sendMobileNotification(
        buildCategoryNotification("ventas", mensaje, detalle, {
          screen: info.screen ?? "caja",
          ...(info.pedidoId ? { pedidoId: info.pedidoId } : {}),
        }),
      );
    }
  } catch {
    /* best-effort: un aviso nunca debe romper el flujo que lo origina */
  }
}
